/**
 * Sales Orders Dashboard KPIs.
 *
 * Backed by ``analytics/views.sales_orders_dashboard`` — pivots on
 * ``purchasing_purchase_order_line.delivery_date`` directly. NO joins to
 * ``purchasing_po_line_schedule``. Covers every PO line regardless of ERP
 * order type — the Sales Orders tab is the unscoped lens.
 */
import api, { type AxiosResponse } from './client';

export interface SalesOrdersDeliveryMetrics {
  // PO-line based OTIF/OTD (no firm window — strict
  // ``receipt_date <= delivery_date`` classification).
  otif_pct: number;
  otd_pct: number;
  total_due: number;
  otif_met: number;
  otd_met: number;
  in_full: number;
  not_delivered: number;
  // GRN-status counters — used for the OTD-mix donut.
  on_time: number;
  early: number;
  late: number;
  total_with_status: number;
  total_grns: number;
}

export interface SalesOrdersCostMetrics {
  total_lines: number;        // PO lines whose delivery_date is in window
  open_lines: number;         // outstanding_qty > 0
  total_value: number;        // quantity * unit_price
  received_value: number;     // received_qty * unit_price
  open_value: number;         // outstanding_qty * unit_price
  arrears_lines: number;      // past-due AND still open
  arrears_value: number;
}

export interface SalesOrdersOrderStatus {
  /** Portfolio-wide open book — Per Week / YTD tabs only. */
  open_count?: number;
  /** On Hold — window-scoped on Current Week/Month; portfolio snapshot elsewhere. */
  on_hold_count: number;
  /** WIP lines due in the viewed week/month window. */
  wip_count?: number;
}

export interface SalesOrdersDeliveryByDay {
  day: 'Sun' | 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat';
  on_time: number;
  early: number;
  late: number;
  pending: number;
}

export interface SalesOrdersPeriodData {
  window_start: string;
  window_end: string;
  /** Full due-date window for Order Status (Current Week = Sun→Sat). */
  order_status_window_end?: string;
  delivery: SalesOrdersDeliveryMetrics;
  cost: SalesOrdersCostMetrics;
  order_status: SalesOrdersOrderStatus;
}

export interface SalesOrdersDeliveryByWeek {
  label: string;
  week_start: string;
  week_end: string;
  on_time: number;
  early: number;
  late: number;
  pending: number;
}

export interface SalesOrdersCurrentWeekData extends SalesOrdersPeriodData {
  deliveries_by_day: SalesOrdersDeliveryByDay[];
  /** Point-in-time count of PO lines by processing stage (On Hold overlaid). */
  status_split?: Record<string, number>;
}

/**
 * Current-Month block — same KPI shape as Current-Week, but the window is a
 * calendar month (1st → end-of-month, capped at today for the current month).
 * Deliveries chart uses ``deliveries_by_week`` (Sunday-start weeks in the month).
 */
export interface SalesOrdersCurrentMonthData extends SalesOrdersPeriodData {
  deliveries_by_week: SalesOrdersDeliveryByWeek[];
  status_split?: Record<string, number>;
}

export interface SalesOrdersWeekTrendPoint {
  label: string;
  week_start: string;
  week_end: string;
  delivery: SalesOrdersDeliveryMetrics;
  cost: SalesOrdersCostMetrics;
}

/**
 * Monthly trend bucket (YTD chart). Same shape as the weekly trend point but
 * keyed on calendar month boundaries. Stream 1 owns the backend payload —
 * this field is intentionally optional so the frontend can render gracefully
 * before the new API ships.
 */
export interface SalesOrdersMonthlyTrendPoint {
  label: string;            // e.g. 'Jun 25'
  month_start: string;      // 'YYYY-MM-DD'
  month_end: string;        // 'YYYY-MM-DD'
  delivery: SalesOrdersDeliveryMetrics;
  cost: SalesOrdersCostMetrics;
}

export interface SalesOrdersPortfolio {
  open_orders_value: number;
  /** Count of live, not-yet-late open lines behind open_orders_value. */
  open_lines?: number;
  arrears_value: number;
  /** Count of overdue live lines (all weeks) behind arrears_value. */
  arrears_lines?: number;
}

export interface SalesOrdersDashboardData {
  supplier_id: number;
  customer_id: number | null;
  part_number: string | null;
  today: string;
  /**
   * OTD grace buffer in days (PlatformSettings.otd_grace_days). A delivery up to
   * this many days after the promised date still counts as on-time. Used by the
   * drill-down's client-side split so it matches the server (donut/by-day).
   * Optional so older cached payloads don't break — defaults to 1.
   */
  otd_grace_days?: number;
  portfolio: SalesOrdersPortfolio;
  order_status: SalesOrdersOrderStatus;
  current_week: SalesOrdersCurrentWeekData;
  ytd: SalesOrdersPeriodData;
  per_week: { weeks: SalesOrdersWeekTrendPoint[] };
  /**
   * Optional monthly trend data appended by Stream 1 backend work. Keep
   * optional so the frontend doesn't crash on responses produced before that
   * deploys.
   */
  monthly?: { months: SalesOrdersMonthlyTrendPoint[] };
}

export interface SalesOrdersDashboardParams {
  supplier_id?: number;
  customer_id?: number;
  part_number?: string;
}

/**
 * Response of the lightweight Current-Week stepper endpoint. Returns ONLY the
 * ``current_week`` block for a single week offset (0 = this week, N = N weeks
 * back), plus the offset echo and the max offset the UI may page to.
 */
export interface SalesOrdersCurrentWeekResponse {
  supplier_id: number;
  customer_id: number | null;
  part_number: string | null;
  week_offset: number;
  max_offset: number;
  current_week: SalesOrdersCurrentWeekData;
}

/**
 * Response of the lightweight Current-Month stepper endpoint. Returns ONLY the
 * ``current_month`` block for a single month offset (0 = this month, N = N
 * calendar months back), plus the offset echo and the max offset the UI may
 * page to.
 */
export interface SalesOrdersCurrentMonthResponse {
  supplier_id: number;
  customer_id: number | null;
  part_number: string | null;
  month_offset: number;
  max_offset: number;
  current_month: SalesOrdersCurrentMonthData;
}

export const salesOrdersDashboardAPI = {
  get(params?: SalesOrdersDashboardParams): Promise<AxiosResponse<SalesOrdersDashboardData>> {
    return api.get('/analytics/sales-orders-dashboard/', { params });
  },

  /**
   * Fetch the Current-Week snapshot for a given week offset.
   *
   * @param weekOffset - Weeks back from this week (0 = current week).
   * @param params - Same supplier/customer/part filters as the full dashboard.
   * @returns The ``current_week`` block for the requested week.
   */
  getCurrentWeek(
    weekOffset: number,
    params?: SalesOrdersDashboardParams,
  ): Promise<AxiosResponse<SalesOrdersCurrentWeekResponse>> {
    return api.get('/analytics/sales-orders-dashboard/current-week/', {
      params: { ...(params ?? {}), week_offset: weekOffset },
    });
  },

  /**
   * Fetch the Current-Month snapshot for a given calendar-month offset.
   *
   * @param monthOffset - Calendar months back from this month (0 = current month).
   * @param params - Same supplier/customer/part filters as the full dashboard.
   * @returns The ``current_month`` block for the requested month.
   */
  getCurrentMonth(
    monthOffset: number,
    params?: SalesOrdersDashboardParams,
  ): Promise<AxiosResponse<SalesOrdersCurrentMonthResponse>> {
    return api.get('/analytics/sales-orders-dashboard/current-month/', {
      params: { ...(params ?? {}), month_offset: monthOffset },
    });
  },
};

// ============================================================================
// Forecast Forward — open sales-order lines bucketed by promised delivery week
// ============================================================================

/** One weekly bucket of the forward forecast. */
export interface SalesOrdersForecastBucket {
  week_start: string;        // 'YYYY-MM-DD'
  week_end: string;          // 'YYYY-MM-DD'
  label: string;             // e.g. 'Week 1'
  line_count: number;        // open PO lines due that week
  order_count: number;       // distinct POs due that week
  expected_qty: number;      // sum of outstanding_qty
  expected_value: number;    // sum of outstanding_qty * unit_price
}

/** Horizon totals for the whole forecast window. */
export interface SalesOrdersForecastTotals {
  line_count: number;
  order_count: number;
  expected_qty: number;
  expected_value: number;
}

export interface SalesOrdersForecastData {
  supplier_id: number;
  customer_id: number | null;
  part_number: string | null;
  today: string;             // 'YYYY-MM-DD'
  weeks: number;             // number of buckets returned
  buckets: SalesOrdersForecastBucket[];
  totals: SalesOrdersForecastTotals;
}

export interface SalesOrdersForecastParams {
  supplier_id?: number;
  customer_id?: number;
  part_number?: string;
  weeks?: number;
}

export const salesOrdersForecastAPI = {
  get(params?: SalesOrdersForecastParams): Promise<AxiosResponse<SalesOrdersForecastData>> {
    return api.get('/analytics/sales-orders-forecast/', { params });
  },
};
